package com.dotdrives.web.pages.donations

import com.dotdrives.controller.NeedsAttentionController
import com.dotdrives.controller.NeedsAttentionUiState
import com.dotdrives.model.AttentionItem
import com.dotdrives.model.AttentionKind
import com.dotdrives.model.Donation
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface NeedsAttentionPanelProps : Props {
    var state: NeedsAttentionUiState
    var controller: NeedsAttentionController
    var canWrite: Boolean
    var onClose: () -> Unit
    var onOpenDonation: (Donation) -> Unit
    var onOpenDrives: () -> Unit
}

val NeedsAttentionPanel = FC<NeedsAttentionPanelProps> { props ->
    val state = props.state

    div {
        className = ClassName("donations-detail-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("donations-attention-panel")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Needs attention"
            onClick = { it.stopPropagation() }

            div {
                className = ClassName("donations-attention-head")
                div {
                    div { className = ClassName("donations-attention-title"); +"Needs attention" }
                    div { className = ClassName("donations-attention-subtitle"); +"Everything blocking clean books, in one queue" }
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("donations-detail-close")
                    ariaLabel = "Close needs attention"
                    onClick = { props.onClose() }
                    +"✕"
                }
            }

            div {
                className = ClassName("donations-attention-body")

                state.error?.let { error ->
                    DotBanner { message = error; onRetry = props.controller::retry }
                }

                when {
                    state.loading && state.items.isEmpty() -> repeat(3) {
                        DotSkeleton { height = "88px" }
                    }
                    // A field member's queue is always empty (has_perm_strict hides both
                    // donations and pledges from that role) -- "Every gift is synced..." would
                    // be an affirmative false claim about org finances, so it gets its own copy
                    // rather than reusing the real all-clear message below.
                    !props.canWrite && state.items.isEmpty() && state.error == null -> DotEmptyState {
                        emoji = "🔒"
                        title = "Donor financials aren't part of your role"
                        body = "Field representatives don't have access to the donations needs-attention queue."
                        primaryAction = null
                    }
                    state.items.isEmpty() && state.error == null -> DotEmptyState {
                        emoji = "✅"
                        title = "Nothing needs attention"
                        body = "Every gift is synced to QuickBooks and every pledge is on track."
                        primaryAction = null
                    }
                    else -> state.items.forEach { item ->
                        AttentionCard {
                            key = item.key.unsafeCast<Key>()
                            this.item = item
                            // Same "now" the controller's clock actually selected these rows
                            // with -- a component reading the wall clock instead could
                            // disagree with asOf and render a "days unpaid" figure the query
                            // itself wasn't computed against, and couldn't be unit-tested.
                            asOf = state.asOf ?: kotlin.time.Instant.fromEpochMilliseconds(0)
                            onOpenDonation = props.onOpenDonation
                            onOpenDrives = props.onOpenDrives
                        }
                    }
                }
            }
        }
    }
}

private external interface AttentionCardProps : Props {
    var item: AttentionItem
    var asOf: kotlin.time.Instant
    var onOpenDonation: (Donation) -> Unit
    var onOpenDrives: () -> Unit
}

private val AttentionCard = FC<AttentionCardProps> { props ->
    val item = props.item
    val copy = attentionCopy(item, props.asOf)
    val isDanger = item.kind == AttentionKind.QboSyncError

    div {
        className = ClassName("donations-attention-item" + if (isDanger) " donations-attention-item-danger" else "")
        div {
            className = ClassName("donations-attention-item-head")
            span {
                className = ClassName("donations-attention-item-title")
                +"${copy.icon} ${copy.title}"
            }
            if (isDanger) {
                DotPill { text = "SYNC ERROR"; tone = com.dotdrives.design.DotTone.Danger }
            }
        }
        div { className = ClassName("donations-attention-item-detail"); +copy.detail }
        div {
            className = ClassName("donations-attention-item-actions")
            when (item.kind) {
                AttentionKind.QboSyncError -> item.donation?.let { donation ->
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("donations-attention-cta")
                        onClick = { props.onOpenDonation(donation) }
                        +"View donation"
                    }
                }
                AttentionKind.UnpaidPledge -> item.pledge?.let { pledge ->
                    if (pledge.driveId != null) {
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("donations-attention-cta")
                            onClick = { props.onOpenDrives() }
                            +"Open drive"
                        }
                    }
                }
            }
        }
    }
}
